import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Paper,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ViewKanban,
  ViewList,
  CalendarMonth,
  Timeline,
  Add,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TaskBoardView, { BoardConfig } from '../../components/Tasks/TaskBoardViewNew';
import TaskListView from '../../components/Tasks/TaskListView';
import { CalendarView } from '../../components/Tasks/CalendarView';
import { TimelineView } from '../../components/Tasks/TimelineView';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import EnhancedTaskCreateDialog from '../../components/Tasks/EnhancedTaskCreateDialog';
import type { ViewMode, SortField, SortOrder, FilterOptions } from '../../components/Tasks/TaskListView';
import { todoService, Task, CreateTaskDTO, UpdateTaskDTO, TaskStatus } from '../../services/api/todo.service';
import { TaskContextEnrichment } from '../../utils/contextCollection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div style={{ padding: 16 }}>
          {children}
        </div>
      )}
    </div>
  );
}

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  // Projects removed - not currently used in this component
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogColumnId, setCreateDialogColumnId] = useState<string | undefined>(undefined);

  // Removed methodology dialog state - methodologies replaced with workflow automation

  // List view state
  const [viewMode] = useState<ViewMode>('list');
  const [sortField, setSortField] = useState<SortField>('due_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filters] = useState<FilterOptions>({});
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());

  // Snackbar state for user feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Board view state
  const isDraggingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const [boardConfig, setBoardConfig] = useState<BoardConfig>({
    columns: {},
    columnOrder: [],
    swimlanes: 'none'
  });

  useEffect(() => {
    loadData();
  }, []);

  // Build board config when we have tasks and statuses
  useEffect(() => {
    console.log('=== Board Config Update UseEffect ===');
    console.log('Tasks count:', tasks.length);
    console.log('Task statuses count:', taskStatuses.length);
    console.log('Is initialized:', isInitializedRef.current);
    console.log('Is dragging:', isDraggingRef.current);

    // Skip if we don't have statuses yet
    if (taskStatuses.length === 0) {
      console.log('No statuses yet, skipping board config update');
      return;
    }

    // Skip reorganizing if we're currently dragging
    if (isDraggingRef.current) {
      console.log('Skipping board reorganization - currently dragging');
      return;
    }

    // Skip if we've already initialized and this is just a re-render
    if (isInitializedRef.current && boardConfig.columns && Object.keys(boardConfig.columns).length > 0) {
      // Only update if the number of tasks has changed
      const currentTaskCount = Object.values(boardConfig.columns).reduce((sum, col) => sum + col.taskIds.length, 0);
      if (currentTaskCount === tasks.length) {
        console.log('Board already initialized with same task count, skipping update');
        return;
      }
    }

    // Update board config to organize tasks into columns
    setBoardConfig(prevConfig => {
      console.log('Setting board config with previous config:', prevConfig);
      // Create a fresh board config from scratch
      const newConfig: BoardConfig = {
        columns: {},
        columnOrder: prevConfig.columnOrder && prevConfig.columnOrder.length > 0
          ? prevConfig.columnOrder
          : taskStatuses.sort((a, b) => a.position - b.position).map(s => s.id),
        swimlanes: prevConfig.swimlanes || 'none'
      };

      // Initialize all columns with empty task arrays (fresh start)
      taskStatuses.forEach(status => {
        newConfig.columns[status.id] = {
          id: status.id,
          title: status.label,
          taskIds: [], // Always start with empty array to prevent duplicates
          color: status.color
        };
      });

      // Debug: Track task distribution
      console.log('=== Task Distribution Debug ===');
      const taskDistribution: Record<string, string[]> = {};

      // Distribute tasks to columns based on their task_status_id
      tasks.forEach((task, index) => {
        console.log(`Processing task ${index + 1}/${tasks.length}: ${task.id} - ${task.title}`);

        // Use task_status_id if available, otherwise try to map from legacy status
        let statusId = task.task_status_id;

        // Fallback: If no task_status_id, try to find matching status by name
        if (!statusId && task.status) {
          const matchingStatus = taskStatuses.find(s => s.name === task.status);
          if (matchingStatus) {
            statusId = matchingStatus.id;
          }
        }

        // Add task to the appropriate column
        if (statusId && newConfig.columns[statusId]) {
          console.log(`  Adding task ${task.id} to column ${statusId}`);
          // Check if task already exists in this column
          if (newConfig.columns[statusId].taskIds.includes(task.id)) {
            console.error(`  DUPLICATE: Task ${task.id} already in column ${statusId}!`);
          } else {
            newConfig.columns[statusId].taskIds.push(task.id);
          }

          // Track distribution for debugging
          if (!taskDistribution[statusId]) {
            taskDistribution[statusId] = [];
          }
          taskDistribution[statusId].push(task.id);
        } else {
          // If no matching status found, add to the default status column
          const defaultStatus = taskStatuses.find(s => s.is_default);
          if (defaultStatus && newConfig.columns[defaultStatus.id]) {
            console.log(`  Adding task ${task.id} to default column ${defaultStatus.id}`);
            if (newConfig.columns[defaultStatus.id].taskIds.includes(task.id)) {
              console.error(`  DUPLICATE: Task ${task.id} already in default column!`);
            } else {
              newConfig.columns[defaultStatus.id].taskIds.push(task.id);
            }

            // Track distribution
            if (!taskDistribution[defaultStatus.id]) {
              taskDistribution[defaultStatus.id] = [];
            }
            taskDistribution[defaultStatus.id].push(task.id);
          } else {
            console.warn(`  Could not find column for task ${task.id}`);
          }
        }
      });

      console.log('=== Final Board Config ===');
      Object.entries(newConfig.columns).forEach(([id, col]) => {
        console.log(`Column ${col.title} (${id}): ${col.taskIds.length} tasks`);
        const uniqueTasks = new Set(col.taskIds);
        if (col.taskIds.length !== uniqueTasks.size) {
          console.error(`DUPLICATES IN COLUMN ${col.title}!`);
          const duplicates = col.taskIds.filter((taskId, index) => col.taskIds.indexOf(taskId) !== index);
          console.error('Duplicate task IDs in column:', duplicates);
        }
      });

      // Check for tasks appearing in multiple columns
      const allTaskIds: string[] = [];
      Object.values(newConfig.columns).forEach(col => {
        allTaskIds.push(...col.taskIds);
      });
      const taskIdToColumns: Record<string, string[]> = {};
      Object.entries(newConfig.columns).forEach(([colId, col]) => {
        col.taskIds.forEach(taskId => {
          if (!taskIdToColumns[taskId]) {
            taskIdToColumns[taskId] = [];
          }
          taskIdToColumns[taskId].push(colId);
        });
      });
      Object.entries(taskIdToColumns).forEach(([taskId, columnIds]) => {
        if (columnIds.length > 1) {
          console.error(`Task ${taskId} appears in multiple columns:`, columnIds);
        }
      });

      isInitializedRef.current = true;
      return newConfig;
    });
  }, [tasks, taskStatuses, boardConfig.columns]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksRes, statusesRes] = await Promise.all([
        todoService.getTasks(),
        todoService.getTaskStatuses()
      ]);

      // Debug: Check for duplicates in the backend response
      console.log('=== Backend Response Debug ===');
      console.log('Total tasks from backend:', tasksRes.length);
      const taskIds = tasksRes.map(t => t.id);
      const uniqueTaskIds = new Set(taskIds);
      if (taskIds.length !== uniqueTaskIds.size) {
        console.error('DUPLICATE TASKS FROM BACKEND!');
        const duplicates = taskIds.filter((id, index) => taskIds.indexOf(id) !== index);
        console.error('Duplicate task IDs:', duplicates);
        tasksRes.forEach(task => {
          if (duplicates.includes(task.id)) {
            console.error('Duplicate task details:', task);
          }
        });
      } else {
        console.log('No duplicates in backend response ✓');
      }

      setTasks(tasksRes);
      setTaskStatuses(statusesRes);

      console.log('LoadData completed: loaded', tasksRes.length, 'tasks and', statusesRes.length, 'statuses');
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCreate = async (taskData: CreateTaskDTO, context?: TaskContextEnrichment) => {
    try {
      // Enrich task data with context if available
      let enrichedTaskData = { ...taskData };

      if (context) {
        // Add context metadata to the task
        enrichedTaskData.metadata = {
          ...enrichedTaskData.metadata,
          context: {
            activityType: context.activityType,
            userIntent: context.userIntent,
            cognitiveDemand: context.cognitiveDemand,
            environmentalFactors: context.environmentalFactors,
            urgencyIndicators: context.urgencyIndicators,
            collectedAt: new Date().toISOString()
          }
        };
      }

      const newTask = await todoService.createTask(enrichedTaskData);
      setTasks(prev => [...prev, newTask]);

      // Show success message
      setSnackbar({
        open: true,
        message: `Task "${newTask.title}" created successfully!`,
        severity: 'success'
      });

      console.log('Task created with context:', context ? 'Yes' : 'No');
    } catch (error) {
      console.error('Failed to create task:', error);

      // Show error message to user
      const errorMessage = error instanceof Error ? error.message : 'Failed to create task. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleQuickTaskCreate = async (taskData: CreateTaskDTO, columnId?: string) => {
    try {
      // If columnId is provided, set the task_status_id
      if (columnId) {
        taskData.task_status_id = columnId;
      }

      const newTask = await todoService.createTask(taskData);
      setTasks(prev => [...prev, newTask]);

      // If columnId is provided, add task to that column in board config
      if (columnId && boardConfig.columns[columnId]) {
        const newConfig = { ...boardConfig };
        newConfig.columns[columnId].taskIds.push(newTask.id);
        setBoardConfig(newConfig);
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleTaskUpdate = async (taskId: string, updates: UpdateTaskDTO) => {
    console.log('💾 handleTaskUpdate called:', { taskId, updates });
    console.log('🔍 Exact updates being sent:', JSON.stringify(updates, null, 2));
    try {
      // If we're updating task_status_id, prevent board rebuild
      if (updates.task_status_id) {
        isDraggingRef.current = true;
      }

      console.log('🌐 Making API call to update task...');
      const updatedTask = await todoService.updateTask(taskId, updates);
      console.log('✅ Task updated successfully:', updatedTask);
      console.log('📋 Updated task_status_id:', updatedTask.task_status_id);
      console.log('📋 Updated status:', updatedTask.status);

      setTasks(prev => prev.map(task => task.id === taskId ? updatedTask : task));

      // Clear flag after a delay if we set it
      if (updates.task_status_id) {
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 500);
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      isDraggingRef.current = false;
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    try {
      await todoService.deleteTask(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));

      // Also update board config to remove task from columns
      setBoardConfig(prevConfig => {
        const newConfig = { ...prevConfig };
        Object.keys(newConfig.columns).forEach(columnId => {
          newConfig.columns[columnId].taskIds = newConfig.columns[columnId].taskIds.filter(id => id !== taskId);
        });
        return newConfig;
      });
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleTaskMove = async (taskId: string, sourceColumn: string, destColumn: string, newIndex: number) => {
    console.log('📋 handleTaskMove called:', { taskId, sourceColumn, destColumn, newIndex });
    // Set dragging flag BEFORE updating board config to prevent reorganization
    isDraggingRef.current = true;

    // Update board config immediately for smooth UI
    setBoardConfig(prevConfig => {
      const newConfig = { ...prevConfig };

      // Remove task from source column
      if (newConfig.columns[sourceColumn]) {
        newConfig.columns[sourceColumn].taskIds = newConfig.columns[sourceColumn].taskIds.filter(id => id !== taskId);
      }

      // Add task to destination column at new index
      if (newConfig.columns[destColumn]) {
        const destTaskIds = [...newConfig.columns[destColumn].taskIds];
        destTaskIds.splice(newIndex, 0, taskId);
        newConfig.columns[destColumn].taskIds = destTaskIds;
      }

      return newConfig;
    });

    // Update the task with the new status ID
    try {
      await handleTaskUpdate(taskId, { task_status_id: destColumn });
    } finally {
      // Clear dragging flag after update completes
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 500); // Small delay to ensure update completes
    }
  };

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
  };

  const handleCloseTaskDetail = () => {
    setSelectedTaskId(null);
  };

  const getSelectedTask = () => {
    return selectedTaskId ? tasks.find(task => task.id === selectedTaskId) || null : null;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBulkAction = async (taskIds: string[], action: string, data?: any) => {
    // Handle bulk actions like delete, update status, etc.
    switch (action) {
      case 'delete':
        for (const taskId of taskIds) {
          try {
            await todoService.deleteTask(taskId);
          } catch (error) {
            console.error(`Failed to delete task ${taskId}:`, error);
          }
        }
        setTasks(prev => prev.filter(task => !taskIds.includes(task.id)));
        setSelectedTasks(new Set());
        break;
      case 'updateStatus':
        for (const taskId of taskIds) {
          try {
            await todoService.updateTask(taskId, { status: data.status });
          } catch (error) {
            console.error(`Failed to update task ${taskId}:`, error);
          }
        }
        await loadData(); // Reload data to get updated tasks
        setSelectedTasks(new Set());
        break;
      default:
        console.warn('Unknown bulk action:', action);
    }
  };

  const handleSortChange = (field: SortField, order: SortOrder) => {
    setSortField(field);
    setSortOrder(order);
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#fafafa'
    }}>
      {/* Header */}
      <Paper style={{ padding: 16, borderRadius: 0 }} elevation={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate('/chat')}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Task Management
            </Typography>
            <Chip
              label={`${tasks.length} tasks`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Add New Task">
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setCreateDialogColumnId(undefined);
                  setShowCreateDialog(true);
                }}
                style={{ backgroundColor: '#E94257' }}
              >
                New Task
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="task view tabs">
          <Tab
            icon={<ViewKanban />}
            iconPosition="start"
            label="Board View"
            id="task-tab-0"
            aria-controls="task-tabpanel-0"
          />
          <Tab
            icon={<ViewList />}
            iconPosition="start"
            label="List View"
            id="task-tab-1"
            aria-controls="task-tabpanel-1"
          />
          <Tab
            icon={<CalendarMonth />}
            iconPosition="start"
            label="Calendar View"
            id="task-tab-2"
            aria-controls="task-tabpanel-2"
          />
          <Tab
            icon={<Timeline />}
            iconPosition="start"
            label="Timeline View"
            id="task-tab-3"
            aria-controls="task-tabpanel-3"
          />
        </Tabs>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TabPanel value={tabValue} index={0}>
          {!loading && boardConfig.columns && Object.keys(boardConfig.columns).length > 0 && (
            <TaskBoardView
              tasks={tasks}
              boardConfig={boardConfig}
              onTaskClick={handleTaskClick}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleQuickTaskCreate}
              onTaskDelete={handleTaskDelete}
              onBoardConfigUpdate={setBoardConfig}
              onTaskMove={handleTaskMove}
              enableQuickAdd={true}
            />
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {!loading && (
            <TaskListView
              tasks={tasks}
              viewMode={viewMode}
              sortBy={sortField}
              sortOrder={sortOrder}
              filterOptions={filters}
              onTaskClick={handleTaskClick}
              onTaskUpdate={handleTaskUpdate}
              onBulkAction={handleBulkAction}
              selectedTasks={selectedTasks}
              onSelectionChange={setSelectedTasks}
              onSortChange={handleSortChange}
              enableDragDrop={true}
            />
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {!loading && (
            <CalendarView
              tasks={tasks}
              onTaskClick={handleTaskClick}
              onTaskCreate={handleTaskCreate}
              onTaskUpdate={handleTaskUpdate}
              onTaskDelete={handleTaskDelete}
              loading={loading}
            />
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {!loading && (
            <TimelineView
              tasks={tasks}
              onTaskClick={handleTaskClick}
              onTaskCreate={handleTaskCreate}
              onTaskUpdate={handleTaskUpdate}
              onTaskDelete={handleTaskDelete}
              loading={loading}
            />
          )}
        </TabPanel>
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={getSelectedTask()}
        open={selectedTaskId !== null}
        onClose={handleCloseTaskDetail}
        onUpdate={handleTaskUpdate}
      />

      {/* Enhanced Task Create Dialog */}
      <EnhancedTaskCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleTaskCreate}
        columnId={createDialogColumnId}
      />

      {/* Methodology components removed - replaced with workflow automation */}

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          style={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TasksPage;